import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UIInputModule } from '@homepower-co/shared-ui-angular';

export type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'color'
  | 'file'
  | 'hidden'
  | 'range';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputVariant =
  | 'default'
  | 'primary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';

export type InputValue = string | number | null | undefined;

@Component({
  selector: 'app-input-search',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, UIInputModule],
  templateUrl: './input-search.component.html',
  styleUrls: ['./input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchComponent),
      multi: true,
    },
  ],
})
export class InputSearchComponent implements ControlValueAccessor {
  private get inputElement(): (HTMLElement & { value?: string; select?: () => void; setSelectionRange?: (start: number, end: number) => void }) | null {
    return this.inputRef?.nativeElement ?? null;
  }

  isStringWithValue(): boolean {
    return typeof this.value === 'string' && this.value.length > 0;
  }
  @Output() searchClick = new EventEmitter<string>();
  @Output() searchInput = new EventEmitter<string>();

  onSearchClick() {
    this.searchClick.emit(String(this.value ?? ''));
  }
  @ViewChild('inputRef') inputRef!: ElementRef<HTMLElement>;

  @Input() type: InputType = 'text';
  @Input() name?: string;
  @Input() placeholder?: string;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() required = false;
  @Input() id?: string;
  @Input() tabindex?: number;
  @Input() autofocus = false;
  @Input() autocomplete?: string;
  @Input() spellcheck?: boolean;
  @Input() autocapitalize?: string;
  @Input() autocorrect?: string;
  @Input() maxLength?: number;
  @Input() step?: string | number;
  @Input() pattern?: string;
  @Input() accept?: string;
  @Input() multiple = false;

  @Input() ariaLabel?: string;
  @Input() ariaDescribedby?: string;
  @Input() role?: string;

  @Input() label?: string;
  @Input() helperText?: string;
  @Input() errorMessages?: string;
  @Input() variant: InputVariant = 'default';
  @Input() size: InputSize = 'md';

  @Input() startIcon?: string;

  @Input() endIcon = 'search';
  @Input() endIconOnClick?: (event: MouseEvent) => void;
  @Output() endIconClick = new EventEmitter<MouseEvent>();

  @Input() help?: string;

  @Input() customClasses?: string;
  @Input() customContainerClasses?: string;
  @Input() dataTestId?: string;
  @Input() control?: AbstractControl;

  @Output() inputChange = new EventEmitter<Event>();
  @Output() inputInput = new EventEmitter<Event>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputKeyDown = new EventEmitter<KeyboardEvent>();
  @Output() inputKeyUp = new EventEmitter<KeyboardEvent>();
  @Output() inputKeyPress = new EventEmitter<KeyboardEvent>();
  @Output() inputClick = new EventEmitter<MouseEvent>();
  @Output() inputMouseEnter = new EventEmitter<MouseEvent>();
  @Output() inputMouseLeave = new EventEmitter<MouseEvent>();

  @Input() value: InputValue = '';
  focused = false;
  inputId = `input-${Math.random().toString(36).substring(2, 9)}`;

  private onTouched: () => void = () => {
    // Mark the component as touched (dirty)
  };
  private onChangeCallback: (value: InputValue) => void = () => {
    // Handle value changes
  };

  writeValue(value: InputValue): void {
    this.value = value === undefined || value === null ? '' : value;
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.value = String(
          this.value === undefined || this.value === null ? '' : this.value,
        );
      }
    });
  }

  registerOnChange(fn: (value: InputValue) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get hasError(): boolean {
    if (this.control) {
      return (
        this.control.invalid && (this.control.touched || this.control.dirty)
      );
    }
    return Boolean(this.errorMessages && this.errorMessages.trim().length > 0);
  }

  get currentVariant(): InputVariant {
    return this.hasError ? 'error' : this.variant;
  }

  get variantClasses(): Record<InputVariant, string> {
    return {
      default: 'border-secondary',
      primary: 'border-primary',
      error: 'border-destructive',
      success: 'border-success',
      warning: 'border-warning',
      info: 'border-info',
    };
  }

  private static readonly sizeClasses: Record<InputSize, string> = {
    sm: 'text-xs h-6 px-2',
    md: 'text-sm h-8 px-2',
    lg: 'text-base h-10 px-3',
  };

  get sizeClasses(): Record<InputSize, string> {
    return InputSearchComponent.sizeClasses;
  }

  private static readonly iconSizeClasses: Record<InputSize, string> = {
    sm: 'text-base', // 16px para mat-icon
    md: 'text-lg', // 18px para mat-icon
    lg: 'text-xl', // 20px para mat-icon
  };

  get iconSizeClasses(): Record<InputSize, string> {
    return InputSearchComponent.iconSizeClasses;
  }

  get containerClasses(): string {
    const base =
      'flex items-center border-2 rounded-md transition-all duration-150 ease-in-out bg-background-light text-white';
    const focus = this.focused ? 'border-opacity-50' : '';
    const variant = this.variantClasses[this.currentVariant];
    const disabled = this.disabled ? 'opacity-50 cursor-not-allowed' : '';
    const custom = this.customContainerClasses ?? '';
    return [base, variant, focus, disabled, custom].filter(Boolean).join(' ');
  }

  get inputClasses(): string {
    const base =
      'bg-transparent outline-none w-full transition-all duration-150 ease-in-out';
    const size = this.sizeClasses[this.size];
    const custom = this.customClasses ?? '';
    return [base, size, custom].filter(Boolean).join(' ');
  }

  get labelClasses(): string {
    const base = 'block font-medium mb-1';
    const size = this.size === 'lg' ? 'text-sm' : 'text-xs';
    return `${base} ${size}`;
  }

  get iconClasses(): string {
    const size = this.iconSizeClasses[this.size];
    return `text-gray-400 flex-shrink-0 ${size}`;
  }

  get resolvedUiInputClasses(): string {
    const base = 'pl-8 pr-16 italic';
    return [base, this.customClasses ?? ''].filter(Boolean).join(' ');
  }

  get resolvedUiInputContainerClasses(): string {
    const sizeClass = this.size === 'lg' ? 'h-12' : this.size === 'sm' ? 'h-7' : 'h-8';
    return [sizeClass, this.customContainerClasses ?? ''].filter(Boolean).join(' ');
  }

  get searchButtonWrapperClasses(): string {
    const sizeClass = this.size === 'lg' ? 'h-[46px] w-10 rounded-r-[6px]' : this.size === 'sm' ? 'h-[26px] w-7 rounded-r-[4px]' : 'h-[30px] w-8 rounded-r-[4px]';
    return `absolute right-[1px] top-1/2 z-10 flex -translate-y-1/2 items-center justify-center border-l border-l-[#bcbcbc] bg-[#707074] ${sizeClass}`;
  }

  private static readonly helperTextClasses = 'text-xs text-gray-500 mt-1';

  get helperTextClasses(): string {
    return InputSearchComponent.helperTextClasses;
  }

  private static readonly errorTextClasses =
    'text-xs text-destructive mt-1 space-y-1';

  get errorTextClasses(): string {
    return InputSearchComponent.errorTextClasses;
  }

  getAriaDescribedBy(): string {
    const parts: string[] = [];
    if (this.ariaDescribedby) parts.push(this.ariaDescribedby);
    if (this.helperText) parts.push(`${this.inputId}-helper`);
    if (this.hasError) parts.push(`${this.inputId}-error`);
    return parts.join(' ');
  }

  focus(): void {
    this.inputRef?.nativeElement?.focus();
  }

  blur(): void {
    this.inputRef?.nativeElement?.blur();
  }

  select(): void {
    this.inputElement?.select?.();
  }

  setSelectionRange(start: number, end: number): void {
    this.inputElement?.setSelectionRange?.(start, end);
  }

  getValue(): InputValue {
    return this.value;
  }

  setValue(value: InputValue): void {
    let stringValue =
      value === undefined || value === null ? '' : String(value);

    // Truncar si excede maxLength
    if (this.maxLength && stringValue.length > this.maxLength) {
      stringValue = stringValue.substring(0, this.maxLength);
    }

    this.value = stringValue;
    this.onChangeCallback(this.value);
    if (this.inputElement) {
      this.inputElement.value = stringValue;
    }
  }

  clear(): void {
    this.setValue('');
    this.searchInput.emit('');
    if (this.inputElement) {
      this.inputElement.value = '';
      const event = new Event('input', { bubbles: true });
      this.inputRef.nativeElement.dispatchEvent(event);
    }
  }

  get nativeElement(): HTMLElement | null {
    return this.inputRef?.nativeElement || null;
  }

  get validity(): ValidityState | undefined {
    return (this.inputRef?.nativeElement as HTMLInputElement | undefined)?.validity;
  }

  get validationMessage(): string {
    return (this.inputRef?.nativeElement as HTMLInputElement | undefined)?.validationMessage || '';
  }

  checkValidity(): boolean {
    return (this.inputRef?.nativeElement as HTMLInputElement | undefined)?.checkValidity() || false;
  }

  reportValidity(): boolean {
    return (this.inputRef?.nativeElement as HTMLInputElement | undefined)?.reportValidity() || false;
  }

  setCustomValidity(message: string): void {
    (this.inputRef?.nativeElement as HTMLInputElement | undefined)?.setCustomValidity(message);
  }

  onUiInput(event: Event): void {
    const customEvent = event as CustomEvent<string | number | null | undefined>;
    let nextValue = customEvent.detail ?? '';
    const normalizedValue = String(nextValue);
    if (this.maxLength && normalizedValue.length > this.maxLength) {
      nextValue = normalizedValue.substring(0, this.maxLength);
      if (this.inputElement) {
        this.inputElement.value = String(nextValue);
      }
    }
    this.value = nextValue;
    this.onChangeCallback(this.value);
    this.inputInput.emit(event);
    this.searchInput.emit(String(this.value ?? ''));
  }

  onUiChange(event: Event): void {
    this.inputChange.emit(event);
  }

  onUiFocus(event: Event): void {
    this.focused = true;
    this.inputFocus.emit(event as FocusEvent);
  }

  onUiBlur(event: Event): void {
    this.focused = false;
    this.onTouched();
    this.inputBlur.emit(event as FocusEvent);
  }

  onUiKeyDown(event: Event): void {
    const keyboardEvent = event as CustomEvent<KeyboardEvent>;
    if (keyboardEvent.detail) {
      this.inputKeyDown.emit(keyboardEvent.detail);
      if (keyboardEvent.detail.key === 'Enter') {
        this.onSearchClick();
      }
    }
  }

  onUiKeyUp(event: Event): void {
    const keyboardEvent = event as CustomEvent<KeyboardEvent>;
    if (keyboardEvent.detail) {
      this.inputKeyUp.emit(keyboardEvent.detail);
    }
  }

  onUiClick(event: Event): void {
    const mouseEvent = event as CustomEvent<MouseEvent>;
    if (mouseEvent.detail) {
      this.inputClick.emit(mouseEvent.detail);
    }
  }

  onUiMouseEnter(event: Event): void {
    const mouseEvent = event as CustomEvent<MouseEvent>;
    if (mouseEvent.detail) {
      this.inputMouseEnter.emit(mouseEvent.detail);
    }
  }

  onUiMouseLeave(event: Event): void {
    const mouseEvent = event as CustomEvent<MouseEvent>;
    if (mouseEvent.detail) {
      this.inputMouseLeave.emit(mouseEvent.detail);
    }
  }
}
